import React, { useState } from 'react';
import axios from 'axios';
import JobForm from './JobForm';
import { getApiUrl } from '../config/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Heading, Text } from '@/Components/ui/typography';
import { Stack, Inline } from '@/Components/ui/layout';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';


const ResumeSubmission = ({ jobId, onClose }) => {
  //const [userProfile, setUserProfile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isJobFormVisible, setIsJobFormVisible] = useState(false);

  const [userid, setUserid] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [customResume, setCustomResume] = useState(null);

  const handleSubmit = async () => {
  
    try {
      const formData = new FormData();
      formData.append("jobId", jobId); 
      formData.append("userId", userid); 
      formData.append("availability", availability); 
      formData.append("resume", customResume);
      // Debug log removed — was leaking userId to console.
      const response = await axios.post(getApiUrl("/jobpage"), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setUserid(response.data.id);
      toast.success("Application submitted successfully!");
      // Debug log removed — was leaking application response data to console.
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting application:", error.response?.data || error.message);
      toast.error("Error submitting application. Check console for details.");
    }
  };
  

  const onBackClick = () => {
    onClose();
  }

  const availabilityOptions = [
    "Yes, I am available to join immediately",
    "I can join within 2 weeks",
    "I need 1 month notice period",
    "I need more than 1 month notice period"
  ];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-slate-200 rounded-3xl shadow-xl w-[90%] max-w-[700px] max-h-[85vh] overflow-y-auto">
        {isSubmitted ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle2 className="text-5xl text-green-600 mb-6" />
            <Button 
              className="bg-gradient-to-r from-green-600 via-green-400 to-green-600 rounded-lg text-white py-3 px-6 text-base cursor-pointer transition-opacity hover:opacity-90"
              onClick={onClose}
            >
              Continue Searching
            </Button>
          </div>
        ) : (
          <>
            {isJobFormVisible ? (
              <JobForm onClose={onClose} />
            ) : (
              <div className='py-2.5 px-5 -ml-[50px] w-[90%] -mt-7.5'>
                <CardHeader className="mb-6 p-0">
                  <div>
                    <Heading level="h1" size="2xl" className="font-semibold text-slate-900 mb-2">
                      Sr. Software Development Engineer
                    </Heading>
                    <Heading level="h2" size="lg" className="text-slate-500">
                      AA Company
                    </Heading>
                  </div>
                </CardHeader>

                <Stack spacing="xl" className="flex flex-col gap-8">
                  <div>
                    <Heading level="h2" size="lg" className="font-medium text-slate-800 mb-4">
                      Your Resume
                    </Heading>
                    <Text size="base" className="text-slate-600 leading-relaxed -mt-2.5">
                      Let recruiters know about you{" "}
                      <a href="#edit" className="text-green-600 no-underline ml-2">Edit profile</a>
                    </Text>
                  </div>

                  <div className="-mt-7.5">
                    <Heading level="h3" size="lg" className="font-medium text-slate-800 mb-4">
                      Confirm Your Availability
                    </Heading>
                    <div className="flex flex-col gap-3">
                      {availabilityOptions.map((option, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <input 
                            type="radio" 
                            id={`option-${index}`} 
                            name="availability"
                            value={option}
                            checked={availability === option}
                            onChange={(e) => setAvailability(e.target.value)}
                            className="w-[18px] h-[18px] accent-green-600"
                          />
                          <Label htmlFor={`option-${index}`} className="text-slate-700 text-[15px] cursor-pointer">
                            {option}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2">
                    <Heading level="h3" size="lg" className="font-medium text-slate-800 mb-4">
                      Custom Resume <span className="text-slate-500 text-sm ml-2">(Optional)</span>
                    </Heading>
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setCustomResume(e.target.files[0])}
                      className="w-full py-3 px-3 border border-dashed border-slate-600 rounded-lg bg-transparent text-white cursor-pointer"
                    />
                  </div>
                </Stack>

                <Inline spacing="md" className="flex justify-end gap-4 mt-8 pt-6 border-t border-slate-800">
                  <Button 
                    variant="outline"
                    className="bg-transparent border border-slate-600 text-slate-900 rounded-lg text-base font-medium cursor-pointer transition-all hover:bg-slate-800"
                    onClick={onBackClick}
                  >
                    Back
                  </Button>
                  <Button 
                    className="bg-gradient-to-r from-green-600 via-green-400 to-green-600 border-none text-white rounded-[10px] w-[15%] ml-[70%] transition-opacity hover:opacity-90"
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                </Inline>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResumeSubmission;
